import isEmpty from 'lodash/isEmpty';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasCashflow, useHasHideBalances } from '~/hooks/useFeatureFlag';
import useRouteMatch from '~/hooks/useRouteMatch';
import { useVisibleBankingConsents } from '~/hooks/useVisibleBankingConsents';
import IconArrowFlow from '~/icons/icon-arrow-flow.svg?react';
import PadlockStar from '~/icons/icon-padlock-star.svg?react';
import { RouteConfig } from '~/routes/routeConfig';
import { useIsSubmenuOpen, useSetSubmenu } from '~/state/navigationSidebar';
import { SubmenuSection } from '~/types/sidebar';
import NavigationSidebarItem from '../NavigationSidebarItem';

interface Props {
  disabled?: boolean;
}

const CashflowItem: React.FC<Props> = ({ disabled }) => {
  const { t } = useTranslation('sidebar');

  const hasCashflow = useHasCashflow();
  const { visibleConsents, consentsQuery } = useVisibleBankingConsents();
  const hasZeroAccounts = consentsQuery.status === 'success' && isEmpty(visibleConsents);
  const hasHideBalances = useHasHideBalances();
  const openSubmenu = useSetSubmenu('cashflow');
  const isSubmenuOpen = useIsSubmenuOpen('cashflow');

  const currentTab = useRouteMatch([RouteConfig.Cashflow.buildLink()]);

  const submenuSections: SubmenuSection[] = useMemo(
    () => [
      {
        label: t('accounts.submenu.overview'),
        items: [{ label: t('cashflow.submenu.cashflow'), href: RouteConfig.Cashflow.buildLink() }],
      },
      {
        label: t('cashflow.submenu.customization'),
        items: [{ label: t('cashflow.submenu.categories'), href: RouteConfig.Categories.buildLink() }],
      },
    ],
    [t],
  );

  if (hasHideBalances) {
    return null;
  }

  return (
    <NavigationSidebarItem
      href={RouteConfig.Cashflow.buildLink()}
      iconComponent={hasCashflow ? IconArrowFlow : PadlockStar}
      isSubmenuOpen={isSubmenuOpen}
      submenuSections={submenuSections}
      onOpenSubmenu={openSubmenu}
      title={t('cashflow.label')}
      disabled={disabled}
      isActive={currentTab !== undefined}
      notificationCount={hasCashflow && hasZeroAccounts ? 1 : undefined}
    />
  );
};

export default memo(CashflowItem);
